/**
 * AdminBackups — full backup management UI.
 *
 * Tabs:
 *  • Backups   — searchable, filterable table of every local + Drive snapshot
 *  • Runs      — every backup attempt with status, duration, error tail
 *  • Logs      — live tail of /var/log/lawos-{backup,gdrive,uptime,autoupdate}.log
 *  • Settings  — cron schedules, retention, notification prefs
 *
 * Access:
 *  • View      — super_admin OR backup_admin
 *  • Download  — super_admin ONLY (confirm modal)
 *  • Restore   — super_admin ONLY (manual VPS command displayed)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    AlertTriangle, CheckCircle2, Cloud, Database, Download, FileArchive,
    HardDrive, Loader2, Play, RefreshCw, Search, Settings as SettingsIcon,
    ShieldAlert, Trash2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";

const fmtKB = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(2)} MB`;
};

const fmtIST = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
        }) + " IST";
    } catch { return iso; }
};

const fmtDuration = (sec) => {
    if (!sec) return "—";
    if (sec < 60) return `${sec.toFixed(1)}s`;
    return `${Math.floor(sec / 60)}m ${(sec % 60).toFixed(0)}s`;
};

const ROLE_CAN_DESTRUCTIVE = (role) => role === "super_admin";

const SourceBadge = ({ source }) => {
    const cls = source === "local"
        ? "bg-blue-100 text-blue-800 border-blue-300"
        : "bg-emerald-100 text-emerald-800 border-emerald-300";
    const Icon = source === "local" ? HardDrive : Cloud;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
            <Icon size={12} /> {source === "local" ? "Local" : "Drive"}
        </span>
    );
};

const StatusBadge = ({ status }) => {
    const map = {
        success: ["bg-emerald-100 text-emerald-800 border-emerald-300", CheckCircle2],
        ok: ["bg-emerald-100 text-emerald-800 border-emerald-300", CheckCircle2],
        failed: ["bg-rose-100 text-rose-800 border-rose-300", AlertTriangle],
    };
    const [cls, Icon] = map[status] || ["bg-slate-100 text-slate-700 border-slate-300", CheckCircle2];
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
            <Icon size={12} /> {status}
        </span>
    );
};

const Card = ({ title, value, hint, icon: Icon, tone = "slate" }) => {
    const toneMap = {
        slate: "border-slate-300",
        emerald: "border-emerald-300 bg-emerald-50",
        rose: "border-rose-300 bg-rose-50",
        blue: "border-blue-300 bg-blue-50",
    };
    return (
        <div className={`p-4 rounded-lg border bg-white ${toneMap[tone]}`} data-testid={`card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
                    <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
                    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
                </div>
                {Icon && <Icon className="text-slate-400" size={20} />}
            </div>
        </div>
    );
};

const Tab = ({ active, label, onClick, count }) => (
    <button
        onClick={onClick}
        data-testid={`tab-${label.toLowerCase()}`}
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            active
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
        }`}
    >
        {label}{typeof count === "number" && <span className="ml-2 text-xs bg-slate-100 px-1.5 py-0.5 rounded">{count}</span>}
    </button>
);

export default function AdminBackups() {
    const [tab, setTab] = useState("backups");
    const [user, setUser] = useState(null);
    const [health, setHealth] = useState(null);
    const [files, setFiles] = useState([]);
    const [sourceFilter, setSourceFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [driveError, setDriveError] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [restoreFile, setRestoreFile] = useState(null);
    const [restoreCmd, setRestoreCmd] = useState("");
    const [rcloneReport, setRcloneReport] = useState(null);
    const [testingRclone, setTestingRclone] = useState(false);

    // -------- profile (for role gating) --------
    useEffect(() => {
        api.get("/auth/me").then(r => setUser(r.data)).catch(() => {});
    }, []);

    // -------- data loaders --------
    const loadHealth = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/backups/health");
            setHealth(data);
        } catch (e) { toast.error(apiError(e)); }
    }, []);

    const loadFiles = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/backups/files", {
                params: { source: sourceFilter, search },
            });
            setFiles(data.files || []);
            setDriveError(data.drive_error);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [sourceFilter, search]);

    const loadAlerts = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/backups/alerts");
            setAlerts(data.alerts || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { loadHealth(); loadAlerts(); }, [loadHealth, loadAlerts]);
    useEffect(() => { if (tab === "backups") loadFiles(); }, [tab, loadFiles]);

    // -------- actions --------
    const [running, setRunning] = useState(null);
    const triggerRun = async (which) => {
        setRunning(which);
        try {
            const { data } = await api.post(`/admin/backups/run/${which}`);
            if (data.status === "success") {
                toast.success(`${which} backup completed in ${data.duration_sec || "?"}s`);
            } else {
                toast.error(`${which} failed: ${(data.error || "").slice(0, 120)}`);
            }
            loadHealth(); loadFiles();
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunning(null); }
    };

    const ackAlert = async (id) => {
        try {
            await api.post(`/admin/backups/alerts/${id}/ack`);
            setAlerts(alerts.filter(a => a._id !== id));
        } catch (e) { toast.error(apiError(e)); }
    };

    const download = async (filename) => {
        if (!ROLE_CAN_DESTRUCTIVE(user?.role)) {
            toast.error("Only super_admin can download backup archives");
            return;
        }
        if (!window.confirm(
            `Download ${filename}?\n\n` +
            "This archive contains ALL your client data including PII. " +
            "Treat the downloaded file as highly confidential.\n\nProceed?"
        )) return;
        try {
            const res = await api.get(`/admin/backups/download/${filename}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded ${filename}`);
        } catch (e) { toast.error(apiError(e)); }
    };

    const restore = async (filename) => {
        if (!ROLE_CAN_DESTRUCTIVE(user?.role)) {
            toast.error("Only super_admin can restore");
            return;
        }
        try {
            const { data } = await api.post("/admin/backups/restore", {
                filename,
                confirm: "I UNDERSTAND THIS WILL OVERWRITE THE DATABASE",
            });
            setRestoreFile(filename);
            setRestoreCmd(data.instructions || "");
        } catch (e) { toast.error(apiError(e)); }
    };

    // -------- derived --------
    const isAdmin = user?.role === "super_admin";
    const newestLocal = health?.local?.newest;
    const newestDrive = health?.gdrive?.newest;

    return (
        <div className="space-y-6" data-testid="admin-backups-page">
            <PageHeader
                title="Backups & Disaster Recovery"
                subtitle="Local mongodump + Google Drive mirror. Auto-scheduled in container."
            />

            {/* ---- Failure alerts banner ---- */}
            {alerts.length > 0 && (
                <div className="space-y-2">
                    {alerts.map(a => (
                        <div key={a._id} className="flex items-start gap-2 p-3 rounded border bg-rose-50 border-rose-300 text-rose-900" data-testid="failure-alert">
                            <ShieldAlert size={18} className="mt-0.5 flex-shrink-0" />
                            <div className="flex-1 text-sm">
                                <div>{a.text}</div>
                                <div className="text-xs opacity-70 mt-0.5">{fmtIST(a.created_at)}</div>
                            </div>
                            <button
                                onClick={() => ackAlert(a._id)}
                                className="text-xs underline hover:no-underline"
                                data-testid={`ack-alert-${a._id}`}
                            >dismiss</button>
                        </div>
                    ))}
                </div>
            )}

            {/* ---- Health summary cards ---- */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card
                    title="Local snapshots"
                    value={health?.local?.count ?? "—"}
                    hint={`Total: ${fmtKB(health?.local?.total_bytes)} · newest ${fmtIST(newestLocal?.modified_iso)}`}
                    icon={HardDrive}
                    tone={newestLocal ? "blue" : "rose"}
                />
                <Card
                    title="Drive snapshots"
                    value={health?.gdrive?.count ?? "—"}
                    hint={
                        health?.gdrive?.error
                            ? `❌ ${health.gdrive.error.slice(0, 80)}`
                            : `Total: ${fmtKB(health?.gdrive?.total_bytes)} · newest ${fmtIST(newestDrive?.modified_iso)}`
                    }
                    icon={Cloud}
                    tone={health?.gdrive?.error ? "rose" : (newestDrive ? "emerald" : "slate")}
                />
                <Card
                    title="Last local run"
                    value={health?.local?.last_run?.status || "no runs yet"}
                    hint={fmtIST(health?.local?.last_run?.started_at)}
                    icon={Database}
                    tone={health?.local?.last_run?.status === "failed" ? "rose" : "slate"}
                />
                <Card
                    title="Last Drive run"
                    value={health?.gdrive?.last_run?.status || "no runs yet"}
                    hint={fmtIST(health?.gdrive?.last_run?.started_at)}
                    icon={Cloud}
                    tone={health?.gdrive?.last_run?.status === "failed" ? "rose" : "slate"}
                />
            </div>

            {/* ---- Quick action bar ---- */}
            <div className="flex flex-wrap gap-2 items-center">
                <button
                    onClick={() => triggerRun("local")}
                    disabled={running === "local"}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50"
                    data-testid="run-local-now-btn"
                >
                    {running === "local" ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
                    Run local backup now
                </button>
                <button
                    onClick={() => triggerRun("gdrive")}
                    disabled={running === "gdrive"}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50"
                    data-testid="run-gdrive-now-btn"
                >
                    {running === "gdrive" ? <Loader2 className="animate-spin" size={14} /> : <Cloud size={14} />}
                    Sync to Drive now
                </button>
                <button
                    onClick={async () => {
                        setTestingRclone(true);
                        try {
                            const { data } = await api.post("/admin/backups/test/rclone");
                            if (data.ok) {
                                toast.success(`rclone OK — ${data.drive_file_count} files on Drive`);
                            } else {
                                toast.error(`rclone failed: ${(data.error || "see diagnostic").slice(0, 120)}`);
                            }
                            // Always open the report so the admin can see exactly which step failed
                            setRcloneReport(data.report || { ok: data.ok, steps: [] });
                        } catch (e) { toast.error(apiError(e)); }
                        finally { setTestingRclone(false); }
                    }}
                    disabled={testingRclone}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                    data-testid="test-rclone-btn"
                >
                    {testingRclone ? <Loader2 className="animate-spin" size={14} /> : null}
                    Test rclone
                </button>
                <button
                    onClick={() => { loadHealth(); loadFiles(); loadAlerts(); }}
                    className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm border border-slate-300 hover:bg-slate-50"
                    data-testid="refresh-btn"
                >
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            {/* ---- Tabs ---- */}
            <div className="border-b border-slate-200">
                <Tab active={tab === "backups"} label="Backups" onClick={() => setTab("backups")} count={files.length} />
                <Tab active={tab === "runs"} label="Runs" onClick={() => setTab("runs")} />
                <Tab active={tab === "logs"} label="Logs" onClick={() => setTab("logs")} />
                <Tab active={tab === "settings"} label="Settings" onClick={() => setTab("settings")} />
            </div>

            {tab === "backups" && (
                <BackupsTab
                    files={files} loading={loading} driveError={driveError}
                    sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
                    search={search} setSearch={setSearch}
                    canDestructive={isAdmin}
                    onDownload={download}
                    onRestore={restore}
                />
            )}
            {tab === "runs" && <RunsTab />}
            {tab === "logs" && <LogsTab />}
            {tab === "settings" && <SettingsTab onSaved={loadHealth} />}

            {/* ---- Restore confirmation modal ---- */}
            {restoreCmd && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRestoreCmd("")}>
                    <div className="bg-white rounded-lg p-5 max-w-2xl w-full" onClick={e => e.stopPropagation()} data-testid="restore-modal">
                        <h3 className="text-lg font-semibold flex items-center gap-2"><ShieldAlert className="text-rose-600" size={20} /> Restore from {restoreFile}</h3>
                        <p className="text-sm text-slate-600 mt-2">
                            For safety, restore must run on the VPS terminal. Copy and paste this command:
                        </p>
                        <pre className="mt-3 p-3 rounded bg-slate-900 text-slate-100 text-xs overflow-x-auto">{restoreCmd}</pre>
                        <button onClick={() => setRestoreCmd("")} className="mt-3 px-3 py-1.5 rounded bg-slate-200 text-sm" data-testid="close-restore-modal">Close</button>
                    </div>
                </div>
            )}

            {/* ---- rclone diagnostic modal ---- */}
            {rcloneReport && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRcloneReport(null)}>
                    <div className="bg-white rounded-lg p-5 max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="rclone-report-modal">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                            {rcloneReport.ok
                                ? <CheckCircle2 className="text-emerald-600" size={20} />
                                : <AlertTriangle className="text-rose-600" size={20} />}
                            rclone diagnostic — {rcloneReport.ok ? "all checks passed" : "failure detected"}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                            Runs five checks inside the backend container: binary, config file, registered remotes, Drive auth (about), and folder listing.
                        </p>
                        <div className="mt-4 space-y-3">
                            {(rcloneReport.steps || []).map((s, i) => (
                                <div key={i} className={`rounded border p-3 ${s.ok ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`} data-testid={`rclone-step-${i}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-medium text-sm flex items-center gap-2">
                                            {s.ok
                                                ? <CheckCircle2 className="text-emerald-700" size={14} />
                                                : <AlertTriangle className="text-rose-700" size={14} />}
                                            {s.label}
                                        </div>
                                        <code className="text-xs text-slate-600">exit={s.exit}</code>
                                    </div>
                                    <code className="text-xs text-slate-500 block mt-1 break-all">$ {s.cmd}</code>
                                    {s.stdout && (
                                        <pre className="mt-2 p-2 rounded bg-slate-900 text-slate-100 text-xs overflow-x-auto whitespace-pre-wrap">{s.stdout}</pre>
                                    )}
                                    {s.stderr && (
                                        <pre className="mt-2 p-2 rounded bg-rose-900 text-rose-100 text-xs overflow-x-auto whitespace-pre-wrap">{s.stderr}</pre>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setRcloneReport(null)} className="mt-4 px-3 py-1.5 rounded bg-slate-200 text-sm" data-testid="close-rclone-report">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// Backups Tab — table with filters + search
// =============================================================================
function BackupsTab({ files, loading, driveError, sourceFilter, setSourceFilter, search, setSearch, canDestructive, onDownload, onRestore }) {
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
                    <input
                        type="text" placeholder="Search by filename..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        className="w-full pl-7 pr-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-300"
                        data-testid="search-input"
                    />
                </div>
                <select
                    value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-slate-300 rounded"
                    data-testid="source-filter"
                >
                    <option value="all">All sources</option>
                    <option value="local">Local only</option>
                    <option value="gdrive">Google Drive only</option>
                </select>
            </div>

            {driveError && (
                <div className="p-2 rounded bg-amber-50 border border-amber-300 text-amber-900 text-xs">
                    <strong>Drive listing error:</strong> {driveError}
                </div>
            )}

            <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="text-left p-2 font-medium text-slate-700">Date / Time (IST)</th>
                            <th className="text-left p-2 font-medium text-slate-700">Source</th>
                            <th className="text-left p-2 font-medium text-slate-700">Filename</th>
                            <th className="text-right p-2 font-medium text-slate-700">Size</th>
                            <th className="text-right p-2 font-medium text-slate-700">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={5} className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} />Loading…</td></tr>
                        )}
                        {!loading && files.length === 0 && (
                            <tr><td colSpan={5} className="p-8 text-center text-slate-500">No backups match your filters.</td></tr>
                        )}
                        {!loading && files.map((f, i) => (
                            <tr key={`${f.source}-${f.name}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`backup-row-${i}`}>
                                <td className="p-2 font-mono text-xs">{fmtIST(f.modified_iso)}</td>
                                <td className="p-2"><SourceBadge source={f.source} /></td>
                                <td className="p-2 font-mono text-xs break-all">{f.name}</td>
                                <td className="p-2 text-right tabular-nums">{fmtKB(f.size_bytes)}</td>
                                <td className="p-2 text-right">
                                    {canDestructive && f.source === "local" ? (
                                        <div className="flex gap-1 justify-end">
                                            <button
                                                onClick={() => onDownload(f.name)}
                                                className="p-1 rounded hover:bg-slate-200" title="Download"
                                                data-testid={`download-btn-${i}`}
                                            ><Download size={14} /></button>
                                            <button
                                                onClick={() => onRestore(f.name)}
                                                className="p-1 rounded hover:bg-rose-100 text-rose-700" title="Restore (overwrites DB)"
                                                data-testid={`restore-btn-${i}`}
                                            ><FileArchive size={14} /></button>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-slate-400">{canDestructive ? "Drive — download via rclone" : "view only"}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// =============================================================================
// Runs Tab — chronological list of every backup attempt
// =============================================================================
function RunsTab() {
    const [runs, setRuns] = useState([]);
    const [kind, setKind] = useState("");
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/backups/runs", { params: { kind: kind || undefined, limit: 100 } });
            setRuns(data.runs || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [kind]);
    useEffect(() => { load(); }, [load]);
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <select value={kind} onChange={e => setKind(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-300 rounded" data-testid="runs-filter">
                    <option value="">All kinds</option>
                    <option value="local">Local only</option>
                    <option value="gdrive">Drive only</option>
                </select>
                <button onClick={load} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm border border-slate-300 hover:bg-slate-50" data-testid="runs-refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="text-left p-2">Started (IST)</th>
                            <th className="text-left p-2">Kind</th>
                            <th className="text-left p-2">Status</th>
                            <th className="text-right p-2">Duration</th>
                            <th className="text-right p-2">Size</th>
                            <th className="text-left p-2">By</th>
                            <th className="text-left p-2">Error / Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} />Loading…</td></tr>}
                        {!loading && runs.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No runs recorded yet.</td></tr>}
                        {runs.map((r, i) => (
                            <tr key={r._id} className="border-b border-slate-100" data-testid={`run-row-${i}`}>
                                <td className="p-2 font-mono text-xs">{fmtIST(r.started_at)}</td>
                                <td className="p-2"><SourceBadge source={r.kind === "local" ? "local" : "gdrive"} /></td>
                                <td className="p-2"><StatusBadge status={r.status} /></td>
                                <td className="p-2 text-right tabular-nums text-xs">{fmtDuration(r.duration_sec)}</td>
                                <td className="p-2 text-right tabular-nums text-xs">{fmtKB(r.size_bytes)}</td>
                                <td className="p-2 text-xs">{r.triggered_by}</td>
                                <td className="p-2 text-xs text-rose-700 max-w-md truncate" title={r.error}>{r.error || ""}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// =============================================================================
// Logs Tab — tail of /var/log/lawos-*.log
// =============================================================================
function LogsTab() {
    const [name, setName] = useState("backup");
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/backups/logs/${name}`, { params: { lines: 300 } });
            setContent(data.content || "(empty)");
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [name]);
    useEffect(() => { load(); }, [load]);
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                {["backup", "gdrive", "uptime", "autoupdate"].map(n => (
                    <button
                        key={n} onClick={() => setName(n)}
                        className={`px-3 py-1.5 rounded text-sm border ${name === n ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:bg-slate-50"}`}
                        data-testid={`log-tab-${n}`}
                    >/var/log/lawos-{n}.log</button>
                ))}
                <button onClick={load} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm border border-slate-300 hover:bg-slate-50" data-testid="logs-refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>
            <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded-lg overflow-auto max-h-[500px]" data-testid="log-content">
                {loading ? "Loading..." : content}
            </pre>
        </div>
    );
}

// =============================================================================
// Settings Tab — schedule, retention, notifications
// =============================================================================
function SettingsTab({ onSaved }) {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/admin/backups/settings").then(r => setS(r.data)).catch(e => toast.error(apiError(e)));
    }, []);
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/backups/settings", s);
            setS(data);
            toast.success("Settings saved");
            onSaved?.();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };
    if (!s) return <div className="text-sm text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} />Loading settings…</div>;

    const upd = (k, v) => setS({ ...s, [k]: v });

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2"><HardDrive size={16} /> Local backup schedule</h3>
                <div className="space-y-2 ml-2">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={s.local_enabled} onChange={e => upd("local_enabled", e.target.checked)} data-testid="local-enabled" />
                        Enabled
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-600 block mb-1">Cron schedule (Asia/Kolkata)</label>
                            <input type="text" value={s.local_cron} onChange={e => upd("local_cron", e.target.value)} className="w-full px-2 py-1 text-sm font-mono border border-slate-300 rounded" data-testid="local-cron" />
                            <div className="text-xs text-slate-500 mt-0.5">5 fields: min hr dom mon dow · default <code>0 2 * * *</code> = 02:00 daily</div>
                        </div>
                        <div>
                            <label className="text-xs text-slate-600 block mb-1">Retention (days)</label>
                            <input type="number" min="1" max="365" value={s.local_retention_days} onChange={e => upd("local_retention_days", +e.target.value)} className="w-full px-2 py-1 text-sm border border-slate-300 rounded" data-testid="local-retention" />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2"><Cloud size={16} /> Google Drive sync</h3>
                <div className="space-y-2 ml-2">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={s.gdrive_enabled} onChange={e => upd("gdrive_enabled", e.target.checked)} data-testid="gdrive-enabled" />
                        Enabled
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-600 block mb-1">Cron schedule</label>
                            <input type="text" value={s.gdrive_cron} onChange={e => upd("gdrive_cron", e.target.value)} className="w-full px-2 py-1 text-sm font-mono border border-slate-300 rounded" data-testid="gdrive-cron" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600 block mb-1">Retention (days)</label>
                            <input type="number" min="1" max="3650" value={s.gdrive_retention_days} onChange={e => upd("gdrive_retention_days", +e.target.value)} className="w-full px-2 py-1 text-sm border border-slate-300 rounded" data-testid="gdrive-retention" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600 block mb-1">rclone remote</label>
                            <input type="text" value={s.gdrive_remote} onChange={e => upd("gdrive_remote", e.target.value)} className="w-full px-2 py-1 text-sm font-mono border border-slate-300 rounded" data-testid="gdrive-remote" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600 block mb-1">Drive folder</label>
                            <input type="text" value={s.gdrive_folder} onChange={e => upd("gdrive_folder", e.target.value)} className="w-full px-2 py-1 text-sm font-mono border border-slate-300 rounded" data-testid="gdrive-folder" />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2"><AlertTriangle size={16} /> Failure notifications</h3>
                <div className="space-y-2 ml-2">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={s.notify_in_ui} onChange={e => upd("notify_in_ui", e.target.checked)} data-testid="notify-in-ui" />
                        Show banner on the admin UI
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={s.notify_push} onChange={e => upd("notify_push", e.target.checked)} data-testid="notify-push" />
                        Send push notification to subscribed admins
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={s.notify_email} onChange={e => upd("notify_email", e.target.checked)} data-testid="notify-email" />
                        Send email (requires RESEND_API_KEY in backend env)
                    </label>
                    {s.notify_email && (
                        <div>
                            <label className="text-xs text-slate-600 block mb-1">Notification email</label>
                            <input type="email" value={s.notify_email_to || ""} onChange={e => upd("notify_email_to", e.target.value)} placeholder="admin@lawos.in" className="w-full max-w-xs px-2 py-1 text-sm border border-slate-300 rounded" data-testid="notify-email-to" />
                        </div>
                    )}
                </div>
            </div>

            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded bg-slate-900 text-white text-sm hover:bg-slate-700 disabled:opacity-50" data-testid="save-settings-btn">
                {saving && <Loader2 className="animate-spin" size={14} />}
                Save settings
            </button>
        </div>
    );
}
